import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { ChangePasswordNavigationService } from './change-password-navigation.service';
import { AccessRoute } from '../../shared/types/webview.types';
import { AppRoutes, ROUTE_PATHS, ROUTE_TITLES } from '../../shared/constants/app-routes';
import { TranslateService } from './translate.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  public currentPath = signal('');
  public canGoBack = computed(() => {
    const currentPath = this.currentPath();
    return currentPath !== '/' + ROUTE_PATHS[AppRoutes.HOME] && currentPath !== '/';
  });

  constructor(
    private router: Router,
    private authService: AuthService,
    private changePasswordNavigationService: ChangePasswordNavigationService,
    private translateService: TranslateService
  ) {
    this.router.events.subscribe(() => {
      this.currentPath.set(this.router.url);
    });
  }

  // ===== NAVEGACIÓN BÁSICA =====
  navigateTo(path: string): void {
    this.router.navigate([path]);
  }

  goToHome(): void {
    this.router.navigate([ROUTE_PATHS[AppRoutes.HOME]]);
  }

  goToCreate(): void {
    this.router.navigate([ROUTE_PATHS[AppRoutes.CREATE_INVITATION]]);
  }

  goToDetails(id: string): void {
    this.router.navigate([ROUTE_PATHS[AppRoutes.INVITATION_DETAILS], id]);
  }

  goToChangePassword(): void {
    this.router.navigate([ROUTE_PATHS[AppRoutes.CHANGE_PASSWORD]]);
  }

  goToPersonalInformation(): void {
    this.router.navigate([ROUTE_PATHS[AppRoutes.PERSONAL_INFORMATION]]);
  }

  goToEditPersonalInfo(field: 'name' | 'phone'): void {
    this.router.navigate([ROUTE_PATHS[AppRoutes.EDIT_PERSONAL_INFO], field]);
  }

  goBack(): void {
    // Si estamos en change-password, usar la lógica específica
    if (this.currentPath().startsWith('/' + ROUTE_PATHS[AppRoutes.CHANGE_PASSWORD] + '/')) {
      const handled = this.changePasswordNavigationService.handleBackNavigation();
      if (handled) {
        return;
      }
    }

    // Si estamos en edicion de datos personales, volver a la pantalla de datos personales
    if (this.currentPath().startsWith('/' + ROUTE_PATHS[AppRoutes.EDIT_PERSONAL_INFO] + '/')) {
      this.goToPersonalInformation();
      return;
    }
    
    // Navegación por defecto
    this.goToHome();
  }

  // ===== NAVEGACIÓN CON VALIDACIÓN =====
  navigateToRoute(route: AccessRoute): boolean {
    if (this.authService.isAuthenticated()) {
      switch (route) {
        case AccessRoute.HOME:
          this.goToHome();
          return true;
        case AccessRoute.CREATE_INVITATION:
          this.goToCreate();
          return true;
        default:
          return false;
      }
    }
    return false;
  }

  // ===== UTILIDADES =====
  getPageTitle(): string {
    const currentPath = this.currentPath();
    console.log('currentPath', currentPath);
    
    // Si las traducciones aún se están cargando, retornar un título por defecto
    if (this.translateService.isLoadingTranslations()) {
      return 'Cargando...';
    }
    
    // Para rutas con parámetros como /invitation-details/123
    if (currentPath.startsWith('/' + ROUTE_PATHS[AppRoutes.INVITATION_DETAILS] + '/')) {
      return this.translateService.translate(ROUTE_TITLES[AppRoutes.INVITATION_DETAILS]);
    }

    // Para rutas con parámetros como /edit-personal-info/name o /edit-personal-info/phone
    if (currentPath.startsWith('/' + ROUTE_PATHS[AppRoutes.EDIT_PERSONAL_INFO] + '/')) {
      return this.translateService.translate(ROUTE_TITLES[AppRoutes.EDIT_PERSONAL_INFO]);
    }

    // Remover la barra inicial y normalizar el path
    const normalizedPath = currentPath.replace(/^\//, '').toLowerCase();
    
    // Buscar qué AppRoutes corresponde a este path
    const routeKey = Object.keys(ROUTE_PATHS).find(
      (key) => ROUTE_PATHS[key as AppRoutes].toLowerCase() === normalizedPath
    ) as AppRoutes | undefined;

    if (routeKey && ROUTE_TITLES[routeKey]) {
      return this.translateService.translate(ROUTE_TITLES[routeKey]);
    }

    // Título por defecto
    return this.translateService.translate(ROUTE_TITLES[AppRoutes.HOME]);
  }
}

